/**
 * The CLIENT twin of `supabase/functions/_shared/utm-stamper.ts:58-97`.
 *
 * ## Why a twin exists at all
 *
 * The publisher deliberately never persists the token: "a stored column would be
 * a cache with no reader today and a second source of truth tomorrow"
 * (utm-stamper.ts:41-44). That decision is right, and it has one consequence —
 * inverting token → post requires RECOMPUTING the token, and SQL cannot do it
 * (FNV-1a over UTF-8 bytes). So the post revenue card recomputes one token per
 * `social_post_targets` row and passes the array into `post_attribution_summary`.
 *
 * ## The risk this file carries
 *
 * There are now three copies of one hash: this file, `bio-link/token.ts`, and
 * `_shared/utm-stamper.ts`. If any of them drifts, every token join silently
 * returns zero rows and the card quietly under-reports — indistinguishable from
 * "no sales". That failure has no error, no log and no test that would catch it
 * by construction, which is why `target-token.test.ts` pins both functions to
 * tokens computed from REAL prod (post_id, connection_id) pairs. Do not delete
 * that test to make a refactor pass.
 *
 * Copied byte-for-byte. Do not "improve" the constants.
 */

const FNV_OFFSET = 14695981039346656037n;
const FNV_PRIME = 1099511628211n;
const MASK64 = (1n << 64n) - 1n;
const BASE36_ALPHABET = '0123456789abcdefghijklmnopqrstuvwxyz';
const TOKEN_LENGTH = 8;
const TOKEN_MODULUS = 36n ** BigInt(TOKEN_LENGTH);

/** The utm_content prefix every Mora-stamped link carries. */
export const MORA_TOKEN_PREFIX = 'mora_';

/**
 * The ONE way to build a UTM token key. `connectionId` — never `platform` — is
 * what makes two accounts on the same platform distinguishable; `user_connections`
 * has no unique constraint on `(user_id, platform)`, and keying on platform once
 * collapsed two accounts into one token and credited the wrong one.
 */
export function destinationTokenKey(postId: string, connectionId: string): string {
  return `${postId}:${connectionId}`;
}

/** Deterministic 8-char lowercase base36 token for a destination key. Pure. */
export function targetToken(key: string): string {
  const bytes = new TextEncoder().encode(key);
  let hash = FNV_OFFSET;
  for (const byte of bytes) {
    hash ^= BigInt(byte);
    hash = (hash * FNV_PRIME) & MASK64;
  }
  let value = hash % TOKEN_MODULUS;
  let out = '';
  for (let i = 0; i < TOKEN_LENGTH; i++) {
    out = BASE36_ALPHABET[Number(value % 36n)] + out;
    value /= 36n;
  }
  return out;
}

/**
 * The full `utm_content` value for one destination — what actually lands in
 * `traffic_sources_daily.utm_content` and `order_attribution.first_content`.
 */
export function destinationUtmContent(postId: string, connectionId: string): string {
  return `${MORA_TOKEN_PREFIX}${targetToken(destinationTokenKey(postId, connectionId))}`;
}
